import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, TextField, IconButton, Button, Select, MenuItem, FormControl, InputLabel, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import RefreshIcon from '@mui/icons-material/Refresh';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SaveIcon from '@mui/icons-material/Save';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { DataGrid } from '@mui/x-data-grid';
import { generateClient } from 'aws-amplify/api';
import { listPowerBIDAXQuery } from '../../../graphql_ap/queries';
import './dax.css';

import Editor from '@monaco-editor/react';

function Dax() {
  // State for saved queries
  const [savedQueries, setSavedQueries] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [selectedQuery, setSelectedQuery] = useState(null);
  
  // State for query editor
  const [currentQuery, setCurrentQuery] = useState('');
  const [queryName, setQueryName] = useState('');
  const [selectedDataset, setSelectedDataset] = useState('Well_Compare');
  
  // State for results
  const [queryResults, setQueryResults] = useState(null);
  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [rawJsonResults, setRawJsonResults] = useState('');
  const [activeResultTab, setActiveResultTab] = useState('results');
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copiedToClipboard, setCopiedToClipboard] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  
  // GraphQL client
  const client = generateClient();
  
  // Dataset options
  const datasetOptions = [
    "Well_Compare",
    "US_Activity",
    "Well_Performance",
    "UpstreamSSAS",
    "US_Production_ByVintage_ByState"
  ];
  
  // Sample queries for demonstration
  const sampleQueries = [
    {
      id: '1',
      name: 'Basin Performance Comparison',
      dataset: 'Well_Compare',
      query: `EVALUATE
SUMMARIZE(
    Wells,
    Basin,
    "WellCount", COUNTROWS(Wells),
    "AvgIP30", AVERAGE(Wells[IP30]),
    "AvgEUR", AVERAGE(Wells[EUR])
)
ORDER BY [AvgIP30] DESC`,
      lastSaved: new Date(2025, 2, 23, 8, 15).toISOString()
    },
    {
      id: '2',
      name: 'US Production by Year',
      dataset: 'US_Production_ByVintage_ByState',
      query: `EVALUATE 
SUMMARIZE(
    CALCULATETABLE(
        Completions,
        --States[StateName] = "North Dakota",
        Countries[CountryName] = "United States of America",
        Completions[VintageYear] >= 2010
    ),
    Completions[VintageYear],
    "Cnt", COUNTROWS(Completions),
    --"IP30", AVERAGE(Completions[IP30_boed])
    "IP30", AVERAGE(Completions[IP30_boed_c])
)`,
      lastSaved: new Date(2025, 2, 20, 14, 30).toISOString()
    },
    {
      id: '3',
      name: 'Well Performance Analysis',
      dataset: 'Well_Performance',
      query: `EVALUATE
CALCULATETABLE(
    SUMMARIZE(
        Wells,
        Wells[WellName],
        "AvgProduction", AVERAGE(Production[Value])
    ),
    Production[Year] >= 2020
)`,
      lastSaved: new Date(2025, 2, 21, 9, 15).toISOString()
    },
    {
      id: '4',
      name: 'Activity by State',
      dataset: 'US_Activity',
      query: `EVALUATE
TOPN(
    10,
    SUMMARIZE(
        CALCULATETABLE(
            Activity,
            Activity[Year] = 2024
        ),
        States[StateName],
        "TotalActivity", SUM(Activity[Count])
    ),
    [TotalActivity], DESC
)`,
      lastSaved: new Date(2025, 2, 22, 11, 45).toISOString()
    },
    {
      id: '5',
      name: 'Quarterly Financial Analysis',
      dataset: 'UpstreamSSAS',
      query: `EVALUATE
ADDCOLUMNS(
    SUMMARIZE(
        Finance,
        Finance[Year],
        Finance[Quarter],
        "Revenue", SUM(Finance[Revenue]),
        "Expenses", SUM(Finance[Expenses])
    ),
    "Profit", [Revenue] - [Expenses],
    "Profit Margin", DIVIDE([Revenue] - [Expenses], [Revenue], 0)
)
ORDER BY Finance[Year], Finance[Quarter]`,
      lastSaved: new Date(2025, 2, 23, 10, 30).toISOString()
    }
  ];

  // Load saved queries from localStorage on component mount
  useEffect(() => {
    let storedQueries = JSON.parse(localStorage.getItem('daxQueries') || '[]');
    
    // If no stored queries, initialize with sample queries
    if (storedQueries.length === 0) {
      localStorage.setItem('daxQueries', JSON.stringify(sampleQueries));
      storedQueries = sampleQueries;
      
      // Set the first sample query as the current query
      setCurrentQuery(sampleQueries[0].query);
      setQueryName(sampleQueries[0].name);
      setSelectedDataset(sampleQueries[0].dataset);
      setSelectedQuery(sampleQueries[0]);
    } else if (storedQueries.length > 0) {
      // If there are stored queries, set the first one as the current query
      setCurrentQuery(storedQueries[0].query);
      setQueryName(storedQueries[0].name);
      setSelectedDataset(storedQueries[0].dataset);
      setSelectedQuery(storedQueries[0]);
    }
    
    setSavedQueries(storedQueries);
  }, []);
  
  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZone: 'America/Chicago'
    }).format(date);
  };
  
  // Handle query selection from saved queries
  const handleQuerySelect = (query) => {
    setSelectedQuery(query);
    setCurrentQuery(query.query);
    setQueryName(query.name);
    setSelectedDataset(query.dataset);
  };
  
  // Mock data for demonstration
  const mockResults = {
    'Well_Compare': `{
      "results": [
        {
          "tables": [
            {
              "rows": [
                {"Basin": "Permian", "Formation": "Wolfcamp", "[WellCount]": 5280, "[AvgIP30]": 1250.45, "[AvgEUR]": 850000},
                {"Basin": "Permian", "Formation": "Bone Spring", "[WellCount]": 3750, "[AvgIP30]": 980.32, "[AvgEUR]": 720000},
                {"Basin": "Bakken", "Formation": "Bakken", "[WellCount]": 4200, "[AvgIP30]": 890.78, "[AvgEUR]": 680000},
                {"Basin": "Eagle Ford", "Formation": "Eagle Ford", "[WellCount]": 3900, "[AvgIP30]": 920.56, "[AvgEUR]": 710000},
                {"Basin": "Haynesville", "Formation": "Haynesville", "[WellCount]": 2100, "[AvgIP30]": 1150.21, "[AvgEUR]": 950000},
                {"Basin": "Marcellus", "Formation": "Marcellus", "[WellCount]": 3600, "[AvgIP30]": 880.67, "[AvgEUR]": 750000},
                {"Basin": "DJ Basin", "Formation": "Niobrara", "[WellCount]": 2800, "[AvgIP30]": 750.89, "[AvgEUR]": 620000},
                {"Basin": "Anadarko", "Formation": "SCOOP/STACK", "[WellCount]": 2400, "[AvgIP30]": 820.34, "[AvgEUR]": 680000}
              ]
            }
          ]
        }
      ]
    }`,
    'US_Production_ByVintage_ByState': `{
      "results": [
        {
          "tables": [
            {
              "rows": [
                {"Completions[VintageYear]": 2011, "[Cnt]": 43124, "[IP30]": 221.91},
                {"Completions[VintageYear]": 2010, "[Cnt]": 39455, "[IP30]": 190.32},
                {"Completions[VintageYear]": 2018, "[Cnt]": 30827, "[IP30]": 596.99},
                {"Completions[VintageYear]": 2019, "[Cnt]": 24003, "[IP30]": 809.31},
                {"Completions[VintageYear]": 2015, "[Cnt]": 28199, "[IP30]": 433.58},
                {"Completions[VintageYear]": 2014, "[Cnt]": 44801, "[IP30]": 352.39},
                {"Completions[VintageYear]": 2012, "[Cnt]": 44892, "[IP30]": 241.97},
                {"Completions[VintageYear]": 2013, "[Cnt]": 43349, "[IP30]": 292.48},
                {"Completions[VintageYear]": 2016, "[Cnt]": 16975, "[IP30]": 529.96},
                {"Completions[VintageYear]": 2017, "[Cnt]": 21067, "[IP30]": 643.63},
                {"Completions[VintageYear]": 2021, "[Cnt]": 16952, "[IP30]": 941.36},
                {"Completions[VintageYear]": 2020, "[Cnt]": 13566, "[IP30]": 940.25},
                {"Completions[VintageYear]": 2022, "[Cnt]": 18224, "[IP30]": 984.18},
                {"Completions[VintageYear]": 2023, "[Cnt]": 10294, "[IP30]": 954.40}
              ]
            }
          ]
        }
      ]
    }`,
    'Well_Performance': `{
      "results": [
        {
          "tables": [
            {
              "rows": [
                {"Wells[WellName]": "Well-A", "[AvgProduction]": 1250.45},
                {"Wells[WellName]": "Well-B", "[AvgProduction]": 980.32},
                {"Wells[WellName]": "Well-C", "[AvgProduction]": 1540.78},
                {"Wells[WellName]": "Well-D", "[AvgProduction]": 1120.56},
                {"Wells[WellName]": "Well-E", "[AvgProduction]": 890.21},
                {"Wells[WellName]": "Well-F", "[AvgProduction]": 1350.67},
                {"Wells[WellName]": "Well-G", "[AvgProduction]": 1050.89},
                {"Wells[WellName]": "Well-H", "[AvgProduction]": 1420.34}
              ]
            }
          ]
        }
      ]
    }`,
    'US_Activity': `{
      "results": [
        {
          "tables": [
            {
              "rows": [
                {"States[StateName]": "Texas", "[TotalActivity]": 12500},
                {"States[StateName]": "North Dakota", "[TotalActivity]": 8750},
                {"States[StateName]": "New Mexico", "[TotalActivity]": 6200},
                {"States[StateName]": "Oklahoma", "[TotalActivity]": 5800},
                {"States[StateName]": "Colorado", "[TotalActivity]": 4300},
                {"States[StateName]": "Wyoming", "[TotalActivity]": 3900},
                {"States[StateName]": "Louisiana", "[TotalActivity]": 3200},
                {"States[StateName]": "Pennsylvania", "[TotalActivity]": 2800},
                {"States[StateName]": "Ohio", "[TotalActivity]": 2100},
                {"States[StateName]": "California", "[TotalActivity]": 1900}
              ]
            }
          ]
        }
      ]
    }`,
    'UpstreamSSAS': `{
      "results": [
        {
          "tables": [
            {
              "rows": [
                {"Year": 2020, "Quarter": "Q1", "[Revenue]": 12500000, "[Expenses]": 8750000, "[Profit]": 3750000},
                {"Year": 2020, "Quarter": "Q2", "[Revenue]": 11200000, "[Expenses]": 7900000, "[Profit]": 3300000},
                {"Year": 2020, "Quarter": "Q3", "[Revenue]": 13800000, "[Expenses]": 9200000, "[Profit]": 4600000},
                {"Year": 2020, "Quarter": "Q4", "[Revenue]": 15400000, "[Expenses]": 10100000, "[Profit]": 5300000},
                {"Year": 2021, "Quarter": "Q1", "[Revenue]": 14700000, "[Expenses]": 9800000, "[Profit]": 4900000},
                {"Year": 2021, "Quarter": "Q2", "[Revenue]": 16200000, "[Expenses]": 10500000, "[Profit]": 5700000},
                {"Year": 2021, "Quarter": "Q3", "[Revenue]": 17800000, "[Expenses]": 11300000, "[Profit]": 6500000},
                {"Year": 2021, "Quarter": "Q4", "[Revenue]": 19500000, "[Expenses]": 12200000, "[Profit]": 7300000}
              ]
            }
          ]
        }
      ]
    }`
  };

  // Execute DAX query
  const executeDaxQuery = async () => {
    if (!currentQuery.trim()) {
      setError('Query cannot be empty');
      return;
    }
    
    setLoading(true);
    setError(null);
    setRawJsonResults('');
    
    try {
      // Execute the GraphQL query with the DAX query
      const response = await client.graphql({
        query: listPowerBIDAXQuery,
        variables: {
          dataset_name: selectedDataset,
          dax_query: currentQuery
        }
      });
      
      const jsonResults = response.data.listPowerBIDAXQuery.json_results;
      
      // Store the raw JSON results for display in the JSON tab
      setRawJsonResults(jsonResults);
      
      // Process the results for the table display
      processResults(jsonResults);
    } catch (err) {
      console.error('Error executing DAX query:', err);
      setError('Failed to execute query: ' + (err.message || 'Unknown error'));
      setQueryResults(null);
      setColumns([]);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };
  
  // Process and parse query results
  const processResults = (jsonResults) => {
    try {
      console.log("Raw JSON results:", jsonResults);
      
      // The jsonResults might already be an object, or it might be a string that needs parsing
      let parsedResults;
      
      if (typeof jsonResults === 'string') {
        // Check if the string starts with "results=" which is a common format in some APIs
        if (jsonResults.trim().startsWith('results=')) {
          jsonResults = jsonResults.trim().substring('results='.length);
        }
        
        // Check if the string is in the format {results=[{tables=[{rows=[...]}]}]}
        // This is a non-standard format that uses = instead of : for key-value pairs
        if (jsonResults.includes('results=') && jsonResults.includes('tables=') && jsonResults.includes('rows=')) {
          console.log("Detected non-standard format with = instead of :");
          
          try {
            // Convert the non-standard format to valid JSON
            // 1. Replace = with : for key-value pairs
            // 2. Add quotes around keys
            let convertedJson = jsonResults
              .replace(/(\w+)=/g, '"$1":')  // Replace key= with "key":
              .replace(/\[(\w+)\]/g, '["$1"]'); // Replace [key] with ["key"]
            
            console.log("Converted JSON:", convertedJson);
            parsedResults = JSON.parse(convertedJson);
          } catch (conversionError) {
            console.error("Conversion error:", conversionError);
            
            // If conversion fails, try to extract the rows directly
            try {
              // Extract the rows part using regex
              const rowsMatch = jsonResults.match(/rows=\[(.*)\]/s);
              if (rowsMatch && rowsMatch[1]) {
                const rowsContent = rowsMatch[1];
                
                // Split the rows content by }, { to get individual rows
                const rowStrings = rowsContent.split('}, {');
                
                // Process each row string into a proper object
                const rows = rowStrings.map((rowStr, index) => {
                  // Clean up the row string
                  let cleanRowStr = rowStr;
                  if (index === 0) {
                    cleanRowStr = cleanRowStr.startsWith('{') ? cleanRowStr : '{' + cleanRowStr;
                  }
                  if (index === rowStrings.length - 1) {
                    // For the last row, remove any trailing brackets
                    cleanRowStr = cleanRowStr.replace(/\]\].*$/, '');
                    cleanRowStr = cleanRowStr.endsWith('}') ? cleanRowStr : cleanRowStr + '}';
                  } else {
                    cleanRowStr = cleanRowStr + '}';
                  }
                  
                  // Convert the row string to a proper object
                  const rowObj = {};
                  const keyValuePairs = cleanRowStr.replace(/[{}]/g, '').split(', ');
                  
                  keyValuePairs.forEach(pair => {
                    const equalPos = pair.indexOf('=');
                    if (equalPos > 0) {
                      const key = pair.substring(0, equalPos);
                      // Remove any trailing brackets from the value
                      let value = pair.substring(equalPos + 1).replace(/\]\].*$/, '');
                      
                      // Try to convert numeric values
                      if (!isNaN(value)) {
                        rowObj[key] = parseFloat(value);
                      } else {
                        rowObj[key] = value;
                      }
                    }
                  });
                  
                  return rowObj;
                });
                
                // Set the parsed results to the extracted rows
                parsedResults = { rows };
              } else {
                throw new Error('Could not extract rows from the results');
              }
            } catch (extractionError) {
              console.error("Row extraction error:", extractionError);
              
              // If all else fails, try the standard JSON parsing approaches
              try {
                // Try to parse the string as JSON
                parsedResults = JSON.parse(jsonResults);
              } catch (parseError) {
                console.error("JSON parse error:", parseError);
                
                // Try to clean up the string by removing any non-JSON characters at the beginning
                let cleanedJson = jsonResults.trim();
                
                // Find the first '{' character
                const firstBrace = cleanedJson.indexOf('{');
                if (firstBrace > 0) {
                  cleanedJson = cleanedJson.substring(firstBrace);
                }
                
                // Find the last '}' character
                const lastBrace = cleanedJson.lastIndexOf('}');
                if (lastBrace > 0 && lastBrace < cleanedJson.length - 1) {
                  cleanedJson = cleanedJson.substring(0, lastBrace + 1);
                }
                
                try {
                  parsedResults = JSON.parse(cleanedJson);
                } catch (cleanError) {
                  console.error("Cleaned JSON parse error:", cleanError);
                  
                  // If that fails, try to extract any JSON-like structure from the string
                  const jsonMatch = jsonResults.match(/\{.*\}/s);
                  if (jsonMatch) {
                    try {
                      parsedResults = JSON.parse(jsonMatch[0]);
                    } catch (extractError) {
                      console.error("Extracted JSON parse error:", extractError);
                      
                      // Last resort: try to parse it as a JavaScript object by evaluating it
                      try {
                        // This is a bit risky but might work for some cases
                        // Convert single quotes to double quotes for JSON compatibility
                        const jsObjectString = jsonMatch[0].replace(/'/g, '"');
                        parsedResults = JSON.parse(jsObjectString);
                      } catch (evalError) {
                        console.error("Eval JSON parse error:", evalError);
                        
                        // If all parsing attempts fail, create a simple object with the raw string
                        parsedResults = { 
                          rawResults: jsonResults,
                          error: "Could not parse as JSON"
                        };
                      }
                    }
                  } else {
                    // If we can't find any JSON-like structure, create a simple object with the raw string
                    parsedResults = { 
                      rawResults: jsonResults,
                      error: "Could not extract JSON structure"
                    };
                  }
                }
              }
            }
          }
        } else {
          // Try standard JSON parsing
          try {
            parsedResults = JSON.parse(jsonResults);
          } catch (parseError) {
            console.error("JSON parse error:", parseError);
            
            // Try to clean up the string by removing any non-JSON characters at the beginning
            let cleanedJson = jsonResults.trim();
            
            // Find the first '{' character
            const firstBrace = cleanedJson.indexOf('{');
            if (firstBrace > 0) {
              cleanedJson = cleanedJson.substring(firstBrace);
            }
            
            // Find the last '}' character
            const lastBrace = cleanedJson.lastIndexOf('}');
            if (lastBrace > 0 && lastBrace < cleanedJson.length - 1) {
              cleanedJson = cleanedJson.substring(0, lastBrace + 1);
            }
            
            try {
              parsedResults = JSON.parse(cleanedJson);
            } catch (cleanError) {
              console.error("Cleaned JSON parse error:", cleanError);
              
              // If that fails, try to extract any JSON-like structure from the string
              const jsonMatch = jsonResults.match(/\{.*\}/s);
              if (jsonMatch) {
                try {
                  parsedResults = JSON.parse(jsonMatch[0]);
                } catch (extractError) {
                  console.error("Extracted JSON parse error:", extractError);
                  
                  // Last resort: try to parse it as a JavaScript object by evaluating it
                  try {
                    // This is a bit risky but might work for some cases
                    // Convert single quotes to double quotes for JSON compatibility
                    const jsObjectString = jsonMatch[0].replace(/'/g, '"');
                    parsedResults = JSON.parse(jsObjectString);
                  } catch (evalError) {
                    console.error("Eval JSON parse error:", evalError);
                    
                    // If all parsing attempts fail, create a simple object with the raw string
                    parsedResults = { 
                      rawResults: jsonResults,
                      error: "Could not parse as JSON"
                    };
                  }
                }
              } else {
                // If we can't find any JSON-like structure, create a simple object with the raw string
                parsedResults = { 
                  rawResults: jsonResults,
                  error: "Could not extract JSON structure"
                };
              }
            }
          }
        }
      } else {
        // If it's already an object, use it directly
        parsedResults = jsonResults;
      }
      
      console.log("Parsed results:", parsedResults);
      
      // Handle different possible formats of the results
      let tableRows;
      
      if (parsedResults.results && 
          parsedResults.results[0] && 
          parsedResults.results[0].tables && 
          parsedResults.results[0].tables[0] && 
          parsedResults.results[0].tables[0].rows) {
        // Format from mock data
        tableRows = parsedResults.results[0].tables[0].rows;
      } else if (parsedResults.tables && parsedResults.tables[0] && parsedResults.tables[0].rows) {
        // Alternative format
        tableRows = parsedResults.tables[0].rows;
      } else if (Array.isArray(parsedResults)) {
        // Direct array of results
        tableRows = parsedResults;
      } else if (typeof parsedResults === 'object' && parsedResults !== null) {
        // Try to extract rows from any property that looks like it contains rows
        const possibleRowsProperties = Object.keys(parsedResults).filter(key => 
          Array.isArray(parsedResults[key]) && 
          parsedResults[key].length > 0 && 
          typeof parsedResults[key][0] === 'object'
        );
        
        if (possibleRowsProperties.length > 0) {
          tableRows = parsedResults[possibleRowsProperties[0]];
        } else {
          // If we can't find an array property, treat the object itself as a single row
          tableRows = [parsedResults];
        }
      } else {
        throw new Error('Could not find table rows in results');
      }
      
      console.log("Table rows:", tableRows);
      setQueryResults(tableRows);
      
      // Generate columns dynamically from the first row
      if (tableRows.length > 0) {
        const firstRow = tableRows[0];
        const columnKeys = Object.keys(firstRow);
        
        const gridColumns = columnKeys.map(key => {
          // Extract column name without table name and brackets
          let headerName = key;
          const match = key.match(/\[([^\]]+)\]$/);
          if (match) {
            headerName = match[1];
          }
          
          return {
            field: key,
            headerName: headerName,
            width: 150,
            editable: false,
            // For numeric values, right-align
            type: typeof firstRow[key] === 'number' ? 'number' : 'string',
            // For full column name as tooltip
            renderHeader: (params) => (
              <Tooltip title={key} placement="top">
                <span>{headerName}</span>
              </Tooltip>
            )
          };
        });
        
        setColumns(gridColumns);
        
        // Add id to each row for DataGrid
        const rowsWithIds = tableRows.map((row, index) => ({
          ...row,
          id: index
        }));
        
        setRows(rowsWithIds);
      } else {
        setColumns([]);
        setRows([]);
      }
    } catch (err) {
      console.error('Error processing results:', err);
      setError('Failed to process results: ' + (err.message || 'Unknown error'));
      setQueryResults(null);
      setColumns([]);
      setRows([]);
    }
  };
  
  // Save query to localStorage
  const saveQuery = () => {
    if (!queryName.trim()) {
      setError('Query name cannot be empty');
      return;
    }
    
    const savedQueries = JSON.parse(localStorage.getItem('daxQueries') || '[]');
    const now = new Date().toISOString();
    
    // Check if query with this name already exists
    const existingIndex = savedQueries.findIndex(q => q.name === queryName);
    
    if (existingIndex >= 0) {
      // Update existing query
      savedQueries[existingIndex] = {
        ...savedQueries[existingIndex],
        dataset: selectedDataset,
        query: currentQuery,
        lastSaved: now
      };
    } else {
      // Add new query
      savedQueries.push({
        id: Date.now().toString(),
        name: queryName,
        dataset: selectedDataset,
        query: currentQuery,
        lastSaved: now
      });
    }
    
    localStorage.setItem('daxQueries', JSON.stringify(savedQueries));
    setSavedQueries(savedQueries);
    setSaveDialogOpen(false);
  };
  
  // Delete query from localStorage
  const deleteQuery = (id) => {
    const updatedQueries = savedQueries.filter(query => query.id !== id);
    localStorage.setItem('daxQueries', JSON.stringify(updatedQueries));
    setSavedQueries(updatedQueries);
    
    // If the deleted query was selected, clear selection
    if (selectedQuery && selectedQuery.id === id) {
      setSelectedQuery(null);
    }
  };
  
  // Export results to CSV
  const exportToCSV = () => {
    if (!rows || rows.length === 0) {
      setError('No results to export');
      return;
    }
    
    try {
      // Create CSV header
      const headers = columns.map(col => col.headerName);
      const csvHeader = headers.join(',');
      
      // Create CSV rows
      const csvRows = rows.map(row => {
        return columns.map(col => {
          const value = row[col.field];
          // Handle values with commas by wrapping in quotes
          return typeof value === 'string' && value.includes(',') 
            ? `"${value}"` 
            : value;
        }).join(',');
      });
      
      // Combine header and rows
      const csvContent = [csvHeader, ...csvRows].join('\n');
      
      // Create a blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `${queryName || 'dax_query'}_results.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Error exporting to CSV:', err);
      setError('Failed to export to CSV: ' + (err.message || 'Unknown error'));
    }
  };
  
  // Copy results to clipboard
  const copyToClipboard = async () => {
    if (!rows || rows.length === 0) {
      setError('No results to copy');
      return;
    }
    
    try {
      // Create tab-separated text for clipboard
      const headers = columns.map(col => col.headerName);
      const tsvHeader = headers.join('\t');
      
      const tsvRows = rows.map(row => {
        return columns.map(col => row[col.field]).join('\t');
      });
      
      const tsvContent = [tsvHeader, ...tsvRows].join('\n');
      
      await navigator.clipboard.writeText(tsvContent);
      setCopiedToClipboard(true);
      setTimeout(() => setCopiedToClipboard(false), 2000); // Reset after 2 seconds
    } catch (err) {
      console.error('Failed to copy:', err);
      setError('Failed to copy to clipboard: ' + (err.message || 'Unknown error'));
    }
  };
  
  // Clear search
  const clearSearch = () => {
    setSearchText('');
  };
  
  // Handle key down in search box
  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      clearSearch();
    }
  };
  
  // Filter saved queries based on search text
  const filteredQueries = savedQueries.filter(query => {
    const searchLower = searchText.toLowerCase();
    return query.name.toLowerCase().includes(searchLower);
  });
  
  // Handle editor change
  const handleEditorChange = (value) => {
    setCurrentQuery(value);
  };
  
  // Open save dialog
  const handleSaveClick = () => {
    setSaveDialogOpen(true);
  };
  
  // Render error message if there's an error
  if (error) {
    setTimeout(() => setError(null), 5000); // Clear error after 5 seconds
  }
  
  return (
    <Box className="dax-container">
      <Typography component="h1" className="dax-title">
        DAX
      </Typography>
      
      <Box className="dax-content">
        {/* Left Panel - Saved Queries */}
        <Box className="dax-left-panel">
          <Box className="search-container">
            <TextField
              placeholder="Search saved queries"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onKeyDown={handleKeyDown}
              size="small"
              className="dax-search"
              InputProps={{
                endAdornment: searchText && (
                  <IconButton
                    aria-label="clear search"
                    onClick={clearSearch}
                    size="small"
                    className="dax-search-clear"
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                )
              }}
            />
          </Box>
          
          <Typography variant="subtitle1" className="dax-section-title">
            Saved Queries
          </Typography>
          
          <Box className="dax-saved-queries">
            {filteredQueries.length === 0 ? (
              <Typography variant="body2" className="dax-no-queries">
                No saved queries found
              </Typography>
            ) : (
              filteredQueries.map(query => (
                <Box 
                  key={query.id} 
                  className={`dax-query-item ${selectedQuery && selectedQuery.id === query.id ? 'selected' : ''}`}
                  onClick={() => handleQuerySelect(query)}
                >
                  <Box className="dax-query-info">
                    <Typography variant="body1" className="dax-query-name">
                      {query.name}
                    </Typography>
                    <Typography variant="body2" className="dax-query-dataset">
                      {query.dataset}
                    </Typography>
                    <Typography variant="caption" className="dax-query-date">
                      {formatDate(query.lastSaved)}
                    </Typography>
                  </Box>
                  <IconButton
                    size="small"
                    className="dax-query-delete"
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteQuery(query.id);
                    }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))
            )}
          </Box>
        </Box>
        
        {/* Right Panel */}
        <Box className="dax-right-panel">
          {/* Top Section - Query Editor */}
          <Box className="dax-editor-section">
            <Box className="dax-editor-toolbar">
              <FormControl size="small" className="dax-dataset-select">
                <InputLabel id="dataset-select-label">Dataset</InputLabel>
                <Select
                  labelId="dataset-select-label"
                  value={selectedDataset}
                  label="Dataset"
                  onChange={(e) => setSelectedDataset(e.target.value)}
                >
                  {datasetOptions.map(option => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <Box className="dax-editor-actions">
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<PlayArrowIcon />}
                  onClick={executeDaxQuery}
                  disabled={loading}
                  className="dax-execute-button"
                >
                  Execute
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<SaveIcon />}
                  onClick={handleSaveClick}
                  className="dax-save-button"
                >
                  Save
                </Button>
              </Box>
            </Box>
            
            <Box className="dax-editor-container">
              <Editor
                height="100%"
                defaultLanguage="msdax"
                value={currentQuery}
                onChange={handleEditorChange}
                options={{
                  minimap: { enabled: false },
                  scrollBeyondLastLine: false,
                  fontSize: 12,
                  fontFamily: "'Consolas', 'Monaco', monospace",
                  lineNumbers: "on",
                  automaticLayout: true,
                  wordWrap: "on",
                  tabSize: 2
                }}
              />
            </Box>
          </Box>
          
          {/* Bottom Section - Results */}
          <Box className="dax-results-section">
            <Box className="dax-results-toolbar">
              <Box className="dax-results-tabs">
                <Button 
                  className={`dax-tab ${activeResultTab === 'results' ? 'active' : ''}`}
                  onClick={() => setActiveResultTab('results')}
                >
                  Results
                </Button>
                <Button 
                  className={`dax-tab ${activeResultTab === 'json' ? 'active' : ''}`}
                  onClick={() => setActiveResultTab('json')}
                >
                  JSON
                </Button>
              </Box>
              
              <Box className="dax-results-actions">
                {activeResultTab === 'results' && (
                  <>
                    <Tooltip title={copiedToClipboard ? "Copied!" : "Copy to clipboard"}>
                      <IconButton
                        onClick={copyToClipboard}
                        size="small"
                        className={copiedToClipboard ? "dax-copy-button copied" : "dax-copy-button"}
                        disabled={!rows.length}
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    
                    <Tooltip title="Export to CSV">
                      <IconButton
                        onClick={exportToCSV}
                        size="small"
                        className="dax-export-button"
                        disabled={!rows.length}
                      >
                        <FileDownloadIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </>
                )}
              </Box>
            </Box>
            
            <Box className="dax-results-container">
              {loading ? (
                <Typography className="dax-loading">Loading results...</Typography>
              ) : activeResultTab === 'results' ? (
                rows.length > 0 ? (
                  <DataGrid
                    rows={rows}
                    columns={columns.map(col => ({
                      ...col,
                      // For numeric columns, use a custom valueFormatter to prevent thousand separators
                      ...(col.type === 'number' && {
                        valueFormatter: (params) => {
                          // Return the raw number without formatting
                          return params.value;
                        }
                      })
                    }))}
                    disableRowSelectionOnClick
                    hideFooter={false}
                    disableColumnMenu
                    disableColumnFilter
                    className="dax-results-grid"
                    rowHeight={28}
                    headerHeight={28}
                    localeText={{
                      // Disable thousand separator to avoid commas in numbers
                      MuiTablePagination: {
                        labelDisplayedRows: ({ from, to, count }) => `${from}-${to} of ${count}`
                      }
                    }}
                  />
                ) : (
                  <Typography className="dax-no-results">
                    No results to display. Execute a query to see results.
                  </Typography>
                )
              ) : (
                // JSON tab content
                <Box className="dax-json-container">
                  {rawJsonResults ? (
                    <pre className="dax-json-content">
                      {rawJsonResults}
                    </pre>
                  ) : (
                    <Typography className="dax-no-results">
                      No JSON results to display. Execute a query to see results.
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
      
      {/* Save Dialog */}
      <Dialog open={saveDialogOpen} onClose={() => setSaveDialogOpen(false)}>
        <DialogTitle>Save Query</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Query Name"
            type="text"
            fullWidth
            variant="outlined"
            value={queryName}
            onChange={(e) => setQueryName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveDialogOpen(false)}>Cancel</Button>
          <Button onClick={saveQuery} variant="contained" color="primary">Save</Button>
        </DialogActions>
      </Dialog>
      
      {/* Error Snackbar */}
      {error && (
        <Box className="dax-error-message">
          {error}
        </Box>
      )}
    </Box>
  );
}

export default Dax;
